import React from 'react';
import { Alert, AlertTitle, Box, Link, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const DEFAULT_NEW_URL = 'https://supplier-management-stage.apps.bil.siemens-energy.com/';

export const isMigrationModeEnabled = () => {
  const raw = (process.env.REACT_APP_MIGRATION_BANNER_ENABLED ?? 'true').trim().toLowerCase();
  return raw === 'true' || raw === '1' || raw === 'yes';
};

export const getMigrationNewUrl = () =>
  (process.env.REACT_APP_MIGRATION_NEW_URL || DEFAULT_NEW_URL).trim();

const isEnabled = isMigrationModeEnabled;
const getNewUrl = getMigrationNewUrl;

type MigrationBannerProps = {
  /** Compact layout for login / access-denied cards */
  compact?: boolean;
};

/**
 * Migration notice pointing users to the new Supplier Management platform.
 * Controlled by REACT_APP_MIGRATION_BANNER_ENABLED and REACT_APP_MIGRATION_NEW_URL.
 */
export const MigrationBanner: React.FC<MigrationBannerProps> = ({ compact = false }) => {
  if (!isEnabled()) return null;

  const newUrl = getNewUrl();

  return (
    <Alert
      severity="info"
      variant="outlined"
      sx={{
        width: '100%',
        textAlign: 'left',
        bgcolor: 'rgba(138, 0, 229, 0.04)',
        borderColor: 'rgba(138, 0, 229, 0.35)',
        '& .MuiAlert-icon': { color: '#8A00E5' },
        mb: compact ? 0 : 2,
      }}
    >
      <AlertTitle sx={{ fontWeight: 700, color: '#1a1533', mb: 0.75 }}>
        Supplier Management Tool Migration Update
      </AlertTitle>
      <Typography variant="body2" sx={{ color: '#4b5563', mb: 1, lineHeight: 1.5 }}>
        The Supplier Management Tool application is currently being migrated to a new platform.
        This migration has still been worked to ensure a smooth transition.
      </Typography>
      <Typography variant="body2" sx={{ color: '#4b5563', mb: 1.25, lineHeight: 1.5 }}>
        Please update your bookmarks accordingly.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1533' }}>
          New URL
        </Typography>
        <Link
          href={newUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontWeight: 600,
            color: '#8A00E5',
            wordBreak: 'break-all',
          }}
        >
          {newUrl}
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </Link>
      </Box>
    </Alert>
  );
};

export default MigrationBanner;
